import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { launcherClasses } from '../design-system/launcher';
import { useEntitlement } from '../hooks/useEntitlement';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { useOnboardingStore } from '../stores/onboarding.store';
import SubscriptionModal from './SubscriptionModal';

interface EmpyFloatingButtonProps {
  /** Navigate to /app/empy/${lessonId} instead of /app/empy/general */
  lessonId?: string;
  /** Override click entirely (e.g. for quiz modal) */
  onClick?: () => void;
  /**
   * Positioning: 'above-nav' floats it for the dashboard on desktop (default),
   * 'above-bar' floats it on lesson/quiz pages, 'docked' puts it in the flow
   * of the layout's compact header below 1280 (redesign PR3: floating at
   * right 16px it covered the right end of every card on a phone).
   */
  position?: 'above-nav' | 'above-bar' | 'docked';
}

export default function EmpyFloatingButton({
  lessonId,
  onClick,
  position,
}: EmpyFloatingButtonProps) {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { hasEntitlement, isLoading } = useEntitlement('empy_conversation');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const isOnboarding = useOnboardingStore((s) => s.isActive);
  const currentStep = useOnboardingStore((s) => s.currentStep);
  const toolHintStage = useOnboardingStore((s) => s.toolHintStage);
  const quickTourPhase = useOnboardingStore((s) => s.quickTourPhase);
  const getCurrentQuickTourSection = useOnboardingStore((s) => s.getCurrentQuickTourSection);
  const isEmpySpotlit =
    isOnboarding &&
    (['welcome', 'goal-selection', 'warm-response', 'vulnerability'].includes(currentStep) ||
      toolHintStage === 'refine');
  const isTourIntro = isOnboarding && currentStep === 'quick-tour' && quickTourPhase === 'intro';
  const isEmpyTourTarget = isTourIntro && getCurrentQuickTourSection() === 'empy';
  const isTourDimmed = isTourIntro && !isEmpyTourTarget;

  // Context-aware buttons (lessonId/onClick) skip entitlement loading
  const isContextAware = !!(lessonId || onClick);
  const positionMode = position || 'above-nav';
  const isDocked = positionMode === 'docked';
  if (isLoading && !isContextAware) {
    // Docked, hold the 44px slot: rendering nothing shifted the compact
    // header's row by 44px when the entitlement arrived (#1022 review). No
    // control and no tour hook until there is something to press.
    return isDocked ? (
      <span
        data-testid="empy-launcher-slot"
        aria-hidden="true"
        className={launcherClasses({ docked: true, raised: false, floatClass: '' })}
      />
    ) : null;
  }

  const handleClick = () => {
    // Custom onClick override (e.g. quiz AI tutor modal)
    if (onClick) {
      onClick();
      return;
    }

    // Handle onboarding tour nav click for Empy
    // Don't navigate to the actual Empy page — it checks entitlements and may show
    // a paywall modal. Just advance the tour to the detail phase.
    if (isOnboarding && !isContextAware) {
      if (currentStep === 'quick-tour' && quickTourPhase === 'intro') {
        useOnboardingStore.getState().handleTourNavClick('empy');
      }
      return;
    }

    // Lesson-specific navigation
    if (lessonId) {
      void navigate(`/app/empy/${lessonId}`);
      return;
    }

    if (!hasEntitlement) {
      setShowSubscriptionModal(true);
      return;
    }
    void navigate('/app/empy/general');
  };

  // Floating: fixed at the bottom right. Docked: in the header's flow.
  const className = launcherClasses({
    docked: isDocked,
    raised: isEmpySpotlit || isEmpyTourTarget,
    dimmed: isTourDimmed,
    floatClass:
      positionMode === 'above-bar'
        ? 'bottom-24 right-4'
        : isDesktop
          ? 'bottom-16 right-4'
          : 'bottom-36 right-4',
  });

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-onboarding="empy-button"
        className={className}
        aria-label="Chat with Empy AI"
      >
        <img src="/landing-giraffe.webp" alt="Empy" className="ee-launcher__img" />
      </button>

      {!isContextAware && (
        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
        />
      )}
    </>
  );
}
